
import SafeMotion from '@/components/motion/SafeMotion';
import MotionErrorBoundary from '@/components/motion/MotionErrorBoundary';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { MappedHeroFeaturedProduct } from '@/hooks/useHeroData';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

interface HeroCarouselProps {
  featuredProducts: MappedHeroFeaturedProduct[];
  setApi: (api: CarouselApi) => void;
}

const HeroCarousel = ({ featuredProducts, setApi }: HeroCarouselProps) => {
  return (
    <MotionErrorBoundary>
      <SafeMotion
        className="relative mt-8 md:mt-0"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <Carousel
          setApi={setApi}
          className="w-full max-w-xs mx-auto md:max-w-sm"
          opts={{
            loop: true,
          }}
        >
          <CarouselContent>
            {featuredProducts.map((product, index) => (
              <CarouselItem key={index}>
                <SafeMotion
                  className="relative p-1"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="relative bg-muted/40 backdrop-blur-xl border border-border/20 rounded-3xl p-6 sm:p-8 shadow-2xl hover:shadow-2xl transition-all duration-500 overflow-hidden h-[450px] flex flex-col justify-between">
                    <div className="text-center relative z-10">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-32 h-32 sm:w-48 sm:h-48 mx-auto object-cover rounded-3xl mb-4 sm:mb-6 border border-border/10 bg-white"
                          style={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <SafeMotion
                          className="w-32 h-32 sm:w-48 sm:h-48 mx-auto bg-gradient-to-br from-primary/10 to-transparent rounded-3xl mb-4 sm:mb-6 flex items-center justify-center border border-border/10 relative overflow-hidden"
                          whileHover={{ scale: 1.05, rotate: 5 }}
                        >
                          <SafeMotion
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-luxury-gold/10 to-transparent"
                            animate={{ x: ["-100%", "100%"] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                          >
                            <div />
                          </SafeMotion>
                          <SafeMotion
                            animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
                            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                            className="relative z-10 text-luxury-gold"
                          >
                            <product.icon className="h-16 w-16 sm:h-24 sm:w-24" />
                          </SafeMotion>
                        </SafeMotion>
                      )}

                      <SafeMotion
                        as="h3"
                        className={`font-playfair font-bold mb-2 text-foreground line-clamp-2 ${
                          product.name.length > 25 
                            ? 'text-xl sm:text-2xl' 
                            : product.name.length > 18 
                              ? 'text-xl sm:text-2xl' 
                              : 'text-2xl sm:text-3xl'
                        }`}
                        title={product.name.length > 20 ? product.name : undefined}
                      >
                        {product.name}
                      </SafeMotion>
                      <SafeMotion
                        as="p"
                        className={`font-poppins text-muted-foreground line-clamp-1 ${
                          product.category.length > 15 
                            ? 'text-sm' 
                            : 'text-sm sm:text-base'
                        }`}
                        title={product.category.length > 15 ? product.category : undefined}
                      >
                        {product.category}
                      </SafeMotion>
                      {product.price !== undefined && product.price !== null && (
                        <div className="mt-4 space-y-2">
                          <div className="bg-black/20 backdrop-blur-sm rounded-lg px-3 py-1 inline-block">
                            <p className="font-bold text-lg text-white">
                              ${Number(product.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                          {product.short_description && (
                            <SafeMotion
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.3, duration: 0.4 }}
                              className="mt-2"
                            >
                              <p className="text-sm text-muted-foreground/80 line-clamp-2 px-2">
                                {product.short_description}
                              </p>
                            </SafeMotion>
                          )}
                        </div>
                      )}
                    </div>

                    <SafeMotion
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <Button
                        asChild
                        variant="outline"
                        className="w-full rounded-full backdrop-blur-md bg-background/30 border-2 border-border hover:bg-muted/80"
                      >
                        <Link
                          to={product.url_slug 
                            ? `/product/${product.url_slug}` 
                            : product.product_id 
                              ? `/product/${product.product_id}` 
                              : '/products'
                          }
                          tabIndex={0}
                          aria-label={`View details for ${product.name}`}
                        >
                          View Details
                        </Link>
                      </Button>
                    </SafeMotion>
                  </div>
                </SafeMotion>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </SafeMotion>
    </MotionErrorBoundary>
  );
};

export default HeroCarousel;
